import {Api} from '../api/api';
import {Popup} from '../popup';
import {GoogleCaptcha} from '../captcha/GoogleCaptcha';
import {toggleAttribute} from '../helpers';

class Form
{
    constructor(element) {
        if (!element) {
            return;
        }

        this.container = element;
        this.successCallback = null;

        this.initListeners();
    }

    initListeners() {
        this.captchaField = this.container.querySelector('[data-field="captcha"]');

        if (this.captchaField && !this.captchaField.classList.contains('is-none')) {
            GoogleCaptcha.init();
        }

        this.container.addEventListener('submit', (event) => {
            event.preventDefault();
            this.submit();
        });
    }

    /**
     * @param {CallableFunction} callback
     */
    setSuccessCallback(callback) {
        this.successCallback = callback;
    }

    /** @return {Object} */
    get data() {
        let formData = {};

        Array.from(this.container.querySelectorAll('[name]')).forEach((element) => {
            if (element.getAttribute('type') === 'radio' || element.getAttribute('type') === 'checkbox') {
                if (element.checked) {
                    formData[element.getAttribute('name')] = element.value;
                }
            } else {
                formData[element.getAttribute('name')] = element.value;
            }
        });

        if (this.captchaField && !formData['g-recaptcha-response']) {
            formData['g-recaptcha-response'] = 'empty';
        }

        return formData;
    }

    get action() {
        return this.container.getAttribute('action');
    }

    submit() {
        this.toggleSubmitButtonDisable(true);
        this.clearErrors();

        Api.post(this.action, this.data, (response) => {
            if (response.isFailure()) {
                return this.proceedFailResponse(response)
            }

            if (response.hasMessage()) {
                this.setMessage(response.getMessage());
            }

            if (this.successCallback) {
                this.successCallback(response);
            }

            this.reset();

            let popup = response.get('popup'),
                redirect = response.get('redirect'),
                reload = response.get('reload');

            if (popup) {
                Popup.appendPopupAndToggle(popup.content, popup.style, popup.script);
            }

            if (redirect) {
                location.href = redirect;
            }

            if (reload) {
                window.location.reload(true);
            }
        });
    }

    /**
     * @param {ApiResponse} response
     */
    proceedFailResponse(response) {
        let errors = response.get('errors', {}),
            firstError = response.getErrorMessage();

        if (Object.keys(errors).length) {
            return this.renderErrors(errors);
        }

        if (firstError) {
            return this.setMessage(response.getMessage());
        }

        if (response.hasMessage()) {
            return this.setMessage(response.getMessage());
        }
    }

    /** @return {boolean} */
    resetFieldsValue() {
        return true;
    }

    reset() {
        if (this.resetFieldsValue()) {
            Array.from(this.container.querySelectorAll('[name]:not([type="hidden"])')).forEach((element) => {
                if (element.tagName === 'SELECT') {
                    element.value = element.querySelector('option').value;
                } else {
                    element.value = '';
                }
            });
        }

        this.toggleSubmitButtonDisable(false);

        if (this.captchaField) {
            this.resetReCaptcha();
        }
    }

    showReCaptcha() {
        let reCaptcha = this.captchaField.querySelector('[data-form="re-captcha"]');

        if (reCaptcha) {
            this.captchaField.classList.remove('is-none');
            GoogleCaptcha.load(reCaptcha);
        }
    }

    resetReCaptcha() {
        let reCaptcha = this.captchaField.querySelector('[data-form="re-captcha"]');

        if (reCaptcha.dataset.type === 'hidden') {
            this.captchaField.classList.add('is-none');
        } else {
            GoogleCaptcha.reset();
        }
    }

    /**
     * @param {Boolean} disable
     */
    toggleSubmitButtonDisable(disable) {
        let buttonElement = this.container.querySelector('[data-form="submit"]');

        if (buttonElement) {
            toggleAttribute(buttonElement, 'disabled', disable);
        }
    }

    /**
     * @param {Object} errors
     */
    renderErrors(errors) {
        this.toggleSubmitButtonDisable(false);

        if (this.captchaField) {
            GoogleCaptcha.reset();
        }

        Object.entries(errors).forEach(([field, fieldErrors]) => {
            let firstErrorMessage = fieldErrors[0],
                fieldElement;

            if (!firstErrorMessage) {
                return;
            }

            if (field === 'g-recaptcha-response') {
                if (this.captchaField.classList.contains('is-none')) {
                    this.showReCaptcha();
                    return;
                }

                fieldElement = this.container.querySelector('[data-form="re-captcha"]');
            } else {
                fieldElement = this.container.querySelector(`[name="${field}"]:not([type="hidden"])`);
            }

            if (!fieldElement) {
                return this.setMessage(firstErrorMessage);
            }

            let fieldParentElement = fieldElement.parentNode,
                errorElement = fieldParentElement.querySelector('.validation-error');

            if (errorElement) {
                errorElement.textContent = firstErrorMessage;
            }

            fieldParentElement.classList.add('has-error');
        });
    }

    clearErrors() {
        Array.from(this.container.querySelectorAll('.has-error')).forEach((element) => {
            let errorElement = element.querySelector('.validation-error');

            if (errorElement) {
                errorElement.textContent = '';
            }

            element.classList.remove('has-error');
        });

        this.setMessage('');
    }

    /**
     * @param {String} message
     */
    setMessage(message) {
        let messageElement = this.container.querySelector('[data-form="message"]');

        if (messageElement) {
            messageElement.innerHTML = message;
        }
    }
}

export {Form};
